import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import PlgIcon from "plg/shell/PlgIcon";
import type { PlgIconName } from "plg/shell/PlgIcon";
import { postTileFeedback } from "plg/api/plgHome";
import type { PlgHomeContent } from "plg/api/plgHome";
import { trackPlg } from "plg/analytics/events";

type Tile = PlgHomeContent["howItWorks"]["tiles"][number];

// One answer per tile per session (prototype standardFeedback), so the thanks stays on a second open.
const ANSWERED_KEY = "plgTileFeedback";

const readAnswered = (): Record<string, boolean> => {
  try {
    return JSON.parse(sessionStorage.getItem(ANSWERED_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveAnswered = (tile: string, helpful: boolean) => {
  try {
    sessionStorage.setItem(ANSWERED_KEY, JSON.stringify({ ...readAnswered(), [tile]: helpful }));
  } catch {
    // storage unavailable: the question comes back next time
  }
};

/**
 * A How it works tile's answer (prototype renderStandardPopup): the icon and tile name on one row, its paragraphs,
 * then Did you find this helpful? with a thumb up and down (product 2026-10-31). An answer goes to Segment
 * (standard_tile_feedback) and to the portal Feedback table, and the row thanks them instead.
 */
function HowItWorksPopup({ tile, onClose }: { tile: Tile; onClose: () => void }) {
  const [answered, setAnswered] = useState(() => tile.key in readAnswered());

  const answer = (helpful: boolean) => {
    setAnswered(true);
    saveAnswered(tile.key, helpful);
    trackPlg("standard_tile_feedback", { tile: tile.key, helpful });
    // Feedback must never get in the way of reading the page.
    postTileFeedback(tile.key, helpful).catch(() => {});
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: { className: "std-pop", "aria-label": tile.label } as object,
        // Prototype .scrim: a light aubergine wash, not MUI's grey
        backdrop: { sx: { backgroundColor: "rgba(42, 18, 65, 0.32)" } },
      }}
    >
      <IconButton aria-label="Close" className="std-pop__x" onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <div className="std-pop__head">
        <div className="std-pop__ic">
          <PlgIcon name={tile.icon as PlgIconName} size={22} />
        </div>
        <h2>{tile.label}</h2>
      </div>
      {tile.answer.map((p) => (
        <p key={p}>{p}</p>
      ))}
      <div className="std-pop__foot">
        {answered ? (
          <span className="std-pop__thanks">Thanks, that helps us write better ones.</span>
        ) : (
          <>
            <span className="std-pop__ask">Did you find this helpful?</span>
            <button type="button" className="std-pop__thumb" aria-label="Helpful" onClick={() => answer(true)}>
              <PlgIcon name="thumbUp" size={18} strokeWidth={1.7} />
            </button>
            <button type="button" className="std-pop__thumb" aria-label="Not helpful" onClick={() => answer(false)}>
              <PlgIcon name="thumbDown" size={18} strokeWidth={1.7} />
            </button>
          </>
        )}
      </div>
    </Dialog>
  );
}

export default HowItWorksPopup;
